import React from 'react'
import { pipe } from 'lodash/fp'
import { EllipsisVerticalIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useSettingsSchemasQuery } from '@/entities/core'

import { ActionsDropdown } from '@/shared/components/ActionsDropdown'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import RHFSettingsWidget from '@/shared/components/HookForm/RHFSettingsWidget'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import { Loader } from '@/shared/components/ui/loader'
import {
	getResolvedSchema,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'
import { dotCase } from '@/shared/tools/utils'

export const BusinessSettingsFieldsCard = ({
	prefix,
	actions,
}: {
	prefix?: string
	actions: any[]
}) => {
	const { t } = useTranslation()

	const schemaQuery = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema(`part:business`),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	if (schemaQuery.isLoading) {
		return (
			<div className='flex size-full items-center justify-center'>
				<Loader />
			</div>
		)
	}

	return (
		<Card data-testid='og-settings-business-panel'>
			<CardHeader>
				<CardTitle>
					{t('settings.business.long', { defaultValue: 'Business constrains' })}
				</CardTitle>
				<CardDescription>
					{t('optimization_groups_settings_business_subtitle')}
				</CardDescription>
				<CardAction>
					<ActionsDropdown actions={actions}>
						<Button
							data-testid='pc-actions-popover-trigger'
							variant='ghost'
							size='icon-sm'
						>
							<EllipsisVerticalIcon />
						</Button>
					</ActionsDropdown>
				</CardAction>
			</CardHeader>
			<CardContent>
				<Blanket
					isTinted={schemaQuery.isLoading}
					className='absolute flex items-center justify-center rounded-lg bg-white/30 backdrop-blur-xs dark:bg-black/30'
				>
					<Loader />
				</Blanket>
				<ErrorBoundary>
					<RHFSettingsWidget
						name={dotCase([prefix, 'business'])}
						schema={schemaQuery.data}
					/>
				</ErrorBoundary>
			</CardContent>
		</Card>
	)
}
